/**
 * 购课/账务服务
 * 处理购课记录相关的 API 调用
 * 
 * 注意: 前端称为"账务"，后端 API 使用 purchase-records
 */

import apiClient from './apiClient';
import { API_ENDPOINTS } from '../config/api';

/**
 * 购课服务
 */
const purchaseService = {
  /**
   * 获取购课记录列表（分页）
   * @param {Object} params - 查询参数
   * @param {number} params.page - 页码
   * @param {number} params.per_page - 每页数量
   * @param {string} params.student_name - 学员姓名筛选
   * @param {string} params.course_name - 课程名筛选
   * @param {string} params.payment_method - 支付方式筛选
   * @param {string} params.date_from - 开始日期 (YYYY-MM-DD)
   * @param {string} params.date_to - 结束日期 (YYYY-MM-DD)
   * @returns {Promise<Object>}
   */
  async getList(params = {}) {
    return apiClient.get(API_ENDPOINTS.PURCHASE.LIST, {
      page: 1,
      per_page: 20,
      ...params,
    });
  },

  /**
   * 获取购课记录详情
   * @param {number} id - 记录 ID
   * @returns {Promise<Object>}
   */
  async getById(id) {
    return apiClient.get(API_ENDPOINTS.PURCHASE.DETAIL(id));
  },

  /**
   * 创建购课记录
   * @param {Object} data - 记录数据
   * @param {number} data.student_id - 学员 ID
   * @param {number} data.course_id - 课程 ID
   * @param {number} data.amount - 金额
   * @param {string} data.payment_method - 支付方式
   * @param {string} data.date - 日期 (YYYY-MM-DD)
   * @param {string} data.remarks - 备注
   * @param {number} [data.referrer_id] - 推荐人 ID（可选）
   * @returns {Promise<Object>}
   */
  async create(data) {
    return apiClient.post(API_ENDPOINTS.PURCHASE.CREATE, data);
  },

  /**
   * 更新购课记录
   * @param {number} id - 记录 ID
   * @param {Object} data - 更新数据
   * @returns {Promise<Object>}
   */
  async update(id, data) {
    return apiClient.put(API_ENDPOINTS.PURCHASE.UPDATE(id), data);
  },

  /**
   * 作废购课记录（创建对冲单）
   * @param {number} id - 记录 ID
   * @param {string} reason - 作废原因
   * @param {Array} proof_urls - 作废凭证图片URLs（可选）
   * @returns {Promise<Object>}
   */
  async voidRecord(id, reason, proof_urls = []) {
    return apiClient.post(`/api/purchase-records/${id}/void`, { 
      reason,
      proof_urls 
    });
  },

  /**
   * 删除购课记录
   * @param {number} id - 记录 ID
   * @returns {Promise<Object>}
   */
  async delete(id) {
    return apiClient.delete(API_ENDPOINTS.PURCHASE.DELETE(id));
  },
};

export default purchaseService;
